import React, { useEffect, useState } from 'react';
import { Box, Button, FormControl, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import './NewsDashboard.css';
import Jodit from '../../../components/JoditEditor/Jodit';
import { Article } from '../../../../data_types';
import { fetchArticles } from '../../../backend/articles';

const NewsDashboard = () => {
    const [articleData, setData] = useState<{ articles: Article[] }>({ articles: [] });

    useEffect(() => {
        const loadArticles = async () => {
            const fetchedData = await fetchArticles();
            setData(fetchedData);
        };
        loadArticles();
    }, []);

    const modules = {
        toolbar: {
            container: [
                [{ header: [2, 3, 4, false] }],
                ["bold", "italic", "underline", "blockquote"],
                [{ color: [] }],
                [
                    { list: "ordered" },
                    { list: "bullet" },
                    { indent: "-1" },
                    { indent: "+1" },
                ],
                ["link", "image"],
                ["clean"],
            ],
        },
        clipboard: {
            matchVisual: false, // This ensures that the HTML is not overly simplified
        },
    };

    const formats = [
        "header",
        "font",
        "bold",
        "italic",
        "underline",
        "strike",
        "blockquote",
        "background",
        "code",
        "script",
        "list",
        "bullet",
        "indent",
        "link",
        "image",
        "video",
        "color",
        "clean"
    ];

    const [newArticleTitle, setNewArticleTitle] = useState('');
    const [newArticleDescription, setNewArticleDescription] = useState('');
    const [newArticleImage, setNewArticleImage] = useState<File | null>(null);
    const [deleteArticleId, setDeleteArticleId] = useState('');
    const [articlesPerRow, setArticlesPerRow] = useState('');
    const [useDefaultImage, setUseDefaultImage] = useState(false);

    const articlesBaseUrl = process.env.REACT_APP_ARTICLES_BASE_URL; // Base URL for the API server

    const handleAddArticleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!newArticleTitle || !newArticleDescription || (!newArticleImage && !useDefaultImage)) {
            alert('Please provide all required fields for the article.');
            return;
        }

        const formData = new FormData();
        formData.append('articleTitle', newArticleTitle);
        formData.append('articleDescription', newArticleDescription);
        formData.append('useDefaultImage', useDefaultImage.toString());

        if (!useDefaultImage && newArticleImage) {
            formData.append('image', newArticleImage);
        }

        try {
            const response = await fetch(`${articlesBaseUrl}`, {
                method: 'POST',
                body: formData,
                credentials: 'include',
            });

            if (response.ok) {
                alert('Article added successfully');
                setNewArticleTitle('');
                setNewArticleDescription('');
                setNewArticleImage(null);
                setUseDefaultImage(false);
            } else {
                alert('Failed to add article');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Error adding article');
        }
    };

    const handleDeleteArticleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!deleteArticleId) {
            alert('Please provide the article ID to delete.');
            return;
        }

        try {
            const response = await fetch(`${articlesBaseUrl}/${deleteArticleId}`, {
                method: 'DELETE',
                credentials: 'include',
            });

            if (response.ok) {
                alert('Article deleted successfully');
                setDeleteArticleId('');
            } else {
                alert('Failed to delete article');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Error deleting article');
        }
    };

    const handleUpdateArticlesPerRow = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!articlesPerRow || isNaN(Number(articlesPerRow))) {
            alert('Please provide a valid number for articles per row.');
            return;
        }

        try {
            const response = await fetch(`${articlesBaseUrl}/updateArticlesPerRow`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ articlesPerRow: articlesPerRow }),
                credentials: 'include',
            });

            if (response.ok) {
                alert('articlesPerRow updated successfully');
                setArticlesPerRow('');
            } else {
                alert('Failed to update articlesPerRow');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Error updating articlesPerRow');
        }
    };

    return (
        <Box className="news-dashboard">
            <form onSubmit={handleAddArticleSubmit}>
                <h2>Add a New Article</h2>
                <TextField
                    label="Article Title"
                    value={newArticleTitle}
                    onChange={(e) => setNewArticleTitle(e.target.value)}
                    fullWidth
                    required
                />
                <Jodit placeholder={"Your text here"} value={newArticleDescription} onChange={setNewArticleDescription} />
                <label>
                    <input
                        type="checkbox"
                        checked={useDefaultImage}
                        onChange={() => {
                            setUseDefaultImage(!useDefaultImage)
                        }}
                    />
                    Use Default Image
                </label>
                <div className={`file-input-container ${useDefaultImage ? 'disabled' : ''}`}>
                    <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => setNewArticleImage(e.target.files?.[0] || null)}
                        disabled={useDefaultImage}
                    />
                </div>
                <Button type="submit" variant="contained" color="primary">
                    Add Article
                </Button>
            </form>

            <form onSubmit={handleDeleteArticleSubmit}>
                <h2>Delete a Article</h2>
                <FormControl fullWidth required>
                    <InputLabel id="type-label">Type</InputLabel>
                    <Select
                        labelId="type-label"
                        name="type"
                        value={deleteArticleId}
                        onChange={(e) => setDeleteArticleId(e.target.value)}
                    >
                        {articleData?.articles?.length > 0 ? (
                            articleData?.articles.map((article) => (
                                <MenuItem value={article.displayId}>{article.articleTitle}</MenuItem>
                            ))
                        ) : (
                            <p>No articles available</p>
                        )}
                    </Select>
                </FormControl>
                <Button type="submit" variant="contained" color="secondary">
                    Delete Article
                </Button>
            </form>

            <form onSubmit={handleUpdateArticlesPerRow}>
                <h2>Update Articles Per Row</h2>
                <TextField
                    label="Articles Per Row"
                    value={articlesPerRow}
                    onChange={(e) => setArticlesPerRow(e.target.value)}
                    fullWidth
                    required
                />
                <Button type="submit" variant="contained" color="primary">
                    Update Articles Per Row
                </Button>
            </form>
        </Box>
    );
};

export default NewsDashboard;